import { AxisCode } from '@atlas/types'

/**
 * Whether a proposed catalog code can be used, answered against the taxonomy the session
 * already holds.
 *
 * No endpoint behind it: the bootstrap registry carries every active value's code, and the
 * brand/product axis is where a new catalog code has to be unique. That makes the check
 * instant and typo-proof at the keystroke, which is the whole point — a code is immutable
 * once created, so the first chance to get it right is the only one.
 *
 * A local read cannot see a value created in another session since the last bootstrap, so
 * the server's uniqueness constraint remains the authority; this only stops the mistakes
 * it can see, which is nearly all of them.
 */

/** Mirrors `slugCode` in `classification.schema.ts` — lowercase letters, digits, underscores. */
export const SLUG_PATTERN = /^[a-z0-9_]+$/

export type CodeVerdict =
	| { state: 'empty' }
	| { state: 'invalid'; reason: string }
	| { state: 'taken'; takenBy: string }
	| { state: 'available' }

/** A suggested slug for a name a user has typed. Not enforced — just a head start. */
export const slugify = (name: string): string =>
	name
		.toLowerCase()
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')

export interface RegistryValue {
	code: string
	label: string
}

export function checkCode(code: string, existing: RegistryValue[]): CodeVerdict {
	const trimmed = code.trim()
	if (!trimmed) return { state: 'empty' }
	if (!SLUG_PATTERN.test(trimmed)) {
		return { state: 'invalid', reason: 'Lowercase letters, digits and underscores only.' }
	}
	const clash = existing.find((v) => v.code === trimmed)
	// Names the holder rather than saying "taken": half the time the clash IS the thing the
	// user meant to add, and seeing its name is what tells them so.
	if (clash) return { state: 'taken', takenBy: clash.label }
	return { state: 'available' }
}

/** The axis a catalog code must be unique within. */
export const CATALOG_AXIS = AxisCode.BRAND
